import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { CommonStateService } from '../state/common-state.service';
import { UrlService } from '../state/url.service';
import { BerufeService } from './berufe.service';
import { BildungszielService } from './bildungsziel.service';
import { OrtService } from './ort.service';
import { ReferrerService } from './referrer.service';

@Injectable({
  providedIn: 'root'
})
export class SuchwortService {
  private readonly urlService = inject(UrlService);
  private readonly ortService = inject(OrtService);
  private readonly commonStateService = inject(CommonStateService);
  private readonly bildungszielService = inject(BildungszielService);
  private readonly berufeService = inject(BerufeService);
  private readonly referrerService = inject(ReferrerService);

  public static readonly URL_PARAM_SUCHWORT = 'sw';

  private readonly _tmpSuchwort$: BehaviorSubject<string> = new BehaviorSubject('');

  public suchwort$: Observable<string> = this.urlService
    .getQueryParameterValue(SuchwortService.URL_PARAM_SUCHWORT)
    .pipe(
      // Suchwörter in der URL mit einem Zeichen werden behandelt, als wären sie leer und führen so zu keinem Fehler.
      map((suchwort) => (suchwort && suchwort.length !== 1 ? suchwort : ''))
    );

  public showRequiredInputState$ = combineLatest([
    this.suchwort$,
    this.ortService.ort$,
    this.ortService.umkreis$,
    this.bildungszielService.dkzIds$,
    this.berufeService.isBerufssuche$
  ]).pipe(
    map(([suchwort, ort, umkreis, dkzIds, isBerufssuche]) => {
      const dkzIdsAreEmpty = dkzIds.length === 0 && !isBerufssuche;
      // Wenn eine dkzId gesetzt ist, soll auch gesucht werden. Das ist einer der einsprünge von Newplan.
      return (
        (!suchwort && !ort && dkzIdsAreEmpty) || (dkzIdsAreEmpty && !suchwort && umkreis === null)
      );
    }),
    distinctUntilChanged()
  );

  setSuchwortFuerSpaetereSuche(suchwort: string) {
    this._tmpSuchwort$.next(suchwort);
  }

  triggerSearch() {
    this.setSuchwortUndSucheDirekt(this._tmpSuchwort$.getValue());
  }

  async setSuchwortUndSucheDirekt(suchwort?: string): Promise<void> {
    const currentSuchwort = await firstValueFrom(this.suchwort$);
    const berufe = await firstValueFrom(this.berufeService.ids$);
    const bezeichnung = await firstValueFrom(this.berufeService.bezeichnung$);
    const origin = await firstValueFrom(this.referrerService.berufeRef$);
    // Suchwörter werden nur zugelassen, wenn sie aus mehr als einem Zeichen bestehen.
    if ((suchwort?.length ?? 0) < 2) {
      return;
    }
    if (suchwort != currentSuchwort) {
      this.commonStateService.setSelectedTermin(null);
    }
    await this.urlService.updateUrl({
      [UrlService.AKTUELLE_SEITE]: null,
      // Hier gibt es ein falsches Sonar finding, dass || durch ?? ersetzt werden kann.
      // || und ?? verhält sich an dieser Stelle nicht gleich, da || falsyness betrachtet und '' daher anders
      // evaluiert als ?? welches nur auf null und undefined prüft.
      [SuchwortService.URL_PARAM_SUCHWORT]: suchwort || null, //NOSONAR
      [UrlService.SELECTED_ANGEBOT]: null,
      [UrlService.DISABLE_CORRECTION]: null,
      [BerufeService.URL_PARAM_BERUFE]: suchwort ? null : berufe,
      [ReferrerService.URL_PARAM_REF]: bezeichnung !== suchwort ? null : origin
    });
  }
}
